import { createAction } from '@reduxjs/toolkit'
import { Dispatch, SetStateAction } from 'react'

import { Screen } from 'constants/tracking/screens'
import { FieldErrorType } from 'pages/ItemUpload/types'
import { AssignedPhotoDto, GtmTrackableItemDto } from 'types/dtos'
import { ItemAuthenticityModalModel, UserModel } from 'types/models'

import { FieldName, ItemStatus, stateName } from './constants'

export const fetchItemOfflineVerificationEligibility = createAction(
  `${stateName}/fetchItemOfflineVerificationEligibility`,
)

export const fetchItemSuggestionsRequest = createAction<{
  photoIds: Array<number>
}>(`${stateName}/fetchItemSuggestionsRequest`)

export const fetchBrands = createAction(
  `${stateName}/fetchBrands`,
  ({
    keyword = '',
    includeAllBrands = false,
  }: {
    keyword?: string
    includeAllBrands?: boolean
  }) => ({
    payload: {
      keyword,
      includeAllBrands,
    },
  }),
)

export const fetchPriceSuggestions = createAction(`${stateName}/fetchPriceSuggestions`)

export const fetchItemRequest = createAction<{
  id: number | null
  tempUuid: string
  userId: number
  setItemStatus: Dispatch<SetStateAction<ItemStatus>>
  removeFieldError: (fieldName: FieldName) => void
  isMultipleSizeGroupsABTestEnabled: boolean
}>(`${stateName}/fetchItemRequest`)

type SubmitItemActionArgs = {
  tempUuid: string
  user: Pick<UserModel, 'externalId' | 'email' | 'id'>
  itemStatus: ItemStatus
  screenName: Screen
  saveAsDraft: boolean
  additionalHeaders: { [key: string]: string }
  isItemBumped: boolean
  trackListingToGoogleTagManager: (item: GtmTrackableItemDto) => void
  trackListingToBraze: (args: {
    itemId: number | undefined
    userExternalId: string | undefined | null
  }) => void
  setFieldErrors: Dispatch<SetStateAction<FieldErrorType>>
  showIVSModal: () => void
  showEVSModal: () => void
  assignedPhotos: Array<AssignedPhotoDto>
}

export const submitItem = createAction(
  `${stateName}/submitItem`,
  ({
    user,
    tempUuid,
    itemStatus,
    screenName,
    saveAsDraft,
    isItemBumped,
    additionalHeaders,
    trackListingToGoogleTagManager,
    trackListingToBraze,
    setFieldErrors,
    showIVSModal,
    showEVSModal,
    assignedPhotos,
  }: SubmitItemActionArgs) => ({
    payload: {
      user,
      tempUuid,
      itemStatus,
      screenName,
      additionalHeaders,
      trackListingToGoogleTagManager,
      trackListingToBraze,
      setFieldErrors,
      showIVSModal,
      showEVSModal,
      assignedPhotos,
    },
    meta: {
      saveAsDraft,
      isItemBumped,
    },
  }),
)

export const selectCatalog = createAction<{
  catalogId: number
  itemStatus: ItemStatus
  fieldErrors: FieldErrorType
  setFieldErrors: Dispatch<SetStateAction<FieldErrorType>>
  removeFieldError: (fieldName: FieldName) => void
  setAuthenticityModalContent: Dispatch<SetStateAction<ItemAuthenticityModalModel | null>>
  requestAuthenticityModal: ({
    force,
    modalDataOnly,
    newCatalogId,
  }: {
    force?: boolean
    modalDataOnly?: boolean
    newCatalogId?: number | null
  }) => void
}>(`${stateName}/selectCatalog`)

export const fetchCatalogs = createAction(`${stateName}/fetchCatalogs`)
export const fetchSizeGroupsByCatalog = createAction(`${stateName}/fetchSizeGroupsByCatalog`)

export const fetchPackageSizesRequest = createAction(`${stateName}/fetchPackageSizesRequest`)

export const getDropOffLocationPrompt = createAction<{ userId: number }>(
  `${stateName}/getDropOffLocationPrompt`,
)
